// S6 — Histórico y comparativas (supervisor).
//
// Selector de período (ayer / 3 días / semana / mes) + lista de líneas con
// agregados HOY vs período. Mismas líneas que S1 (scope estructural).
//
// Refactor visual 2026-05-26: tokens consistentes con S1 + L2 (sin colores
// hardcoded como emerald-100/red-800), tabular-nums en cantidades, separadores
// de miles es-CO, header alineado al patrón del resto del MVP.
//
// Delta visual:
//   - ↑ ok si hoy > promedio_dia del período (por encima)
//   - ↓ crit si hoy < promedio_dia (por debajo)
//   - · neutral si delta es 0 o sin data

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { PeriodoComparativa, SupervisorHistoricoLinea } from 'schemas';
import { Card, CardContent } from '../../components/ui/card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { useSupervisorHistorico } from '../../lib/api/queries';

const PERIODOS: Array<{ value: PeriodoComparativa; label: string }> = [
  { value: 'ayer', label: 'Ayer' },
  { value: 'tres_dias', label: '3 días' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes', label: 'Mes' },
];

export function S6HistoricoPage() {
  const { state } = useAuth();
  const [periodo, setPeriodo] = useState<PeriodoComparativa>('ayer');
  const { data, isLoading, isError, error } = useSupervisorHistorico(periodo);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const plantasLabel = data ? data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ') : '';
  const fechaLegible = data ? formatearFechaCorta(data.fecha_hoy) : '';

  return (
    <RoleLayout
      sidebar={<SupervisorSidebar />}
      roleLabel={`Supervisor${plantasLabel ? ` · ${plantasLabel}` : ''}`}
    >
      <div className="flex flex-col gap-4">
        {/* Header alineado al patrón S1: contexto pequeño + título + meta */}
        <header className="flex flex-col gap-1 border-b border-border pb-3">
          {data ? (
            <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              {data.tenant.nombre} · {plantasLabel} · {fechaLegible}
            </p>
          ) : null}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h1 className="text-xl font-medium text-fg">Histórico y comparativas</h1>
            <p className="text-[11px] text-fg-tertiary">
              {data ? data.periodo_label : 'Cargando…'}
            </p>
          </div>
        </header>

        <section className="flex flex-wrap gap-2">
          <SegmentedControl value={periodo} onChange={setPeriodo} options={PERIODOS} />
        </section>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <Spinner className="size-6" />
            <p className="text-sm text-fg-tertiary">Cargando histórico…</p>
          </div>
        ) : isError || !data ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-crit">
              {error instanceof Error ? error.message : 'No pudimos cargar el histórico'}
            </CardContent>
          </Card>
        ) : data.lineas.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-fg-tertiary">
              Sin líneas en tu scope estructural.
            </CardContent>
          </Card>
        ) : (
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.lineas.map((l) => (
              <LineaComparativaCard key={l.linea.id} item={l} />
            ))}
          </section>
        )}

        <footer className="text-center text-[11px] text-fg-tertiary">
          S6 · supervisor · datos hoy {data?.fecha_hoy ?? '—'} vs período
        </footer>
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// LineaComparativaCard — refactor con tokens + tabular-nums + separadores miles
// =============================================================================

function LineaComparativaCard({ item }: { item: SupervisorHistoricoLinea }) {
  const { linea, hoy, comparativa, delta_pct } = item;
  const sinDataPeriodo = comparativa.dias_con_data === 0;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 pt-4">
        {/* Header: nombre + delta-pill */}
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <h3 className="truncate text-sm font-medium text-fg">{linea.nombre}</h3>
            <p className="text-[11px] text-fg-tertiary">{linea.planta.nombre}</p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            <span className="text-[11px] text-fg-tertiary">{linea.codigo}</span>
            <DeltaPill delta={delta_pct} />
          </div>
        </div>

        {/* HOY vs PERÍODO, lado a lado */}
        <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
          <CelaComparativa
            label="HOY"
            cantidad={hoy.cantidad_total}
            bloques={hoy.bloques_capturados}
            paros_count={hoy.paros_count}
            paros_min={hoy.paros_min_totales}
            tone="primary"
          />
          <div className="border-l border-border pl-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
              Período
            </p>
            {sinDataPeriodo ? (
              <p className="mt-1 text-sm text-fg-tertiary">Sin datos</p>
            ) : (
              <>
                <p
                  className="text-xl font-medium leading-none text-fg-secondary"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatearMiles(Math.round(comparativa.cantidad_total))}
                </p>
                <p className="mt-0.5 text-[11px] text-fg-tertiary">
                  {comparativa.dias_con_data} día
                  {comparativa.dias_con_data === 1 ? '' : 's'} · prom{' '}
                  <span className="tabular-nums">
                    {formatearMiles(Math.round(comparativa.cantidad_promedio_dia))}
                  </span>
                  /día
                </p>
                {comparativa.paros_count > 0 ? (
                  <p className="mt-0.5 text-[11px] text-fg-tertiary">
                    {comparativa.paros_count} paro(s) ·{' '}
                    <span className="tabular-nums">
                      {Math.round(comparativa.paros_min_totales)}
                    </span>{' '}
                    min
                  </p>
                ) : null}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CelaComparativa({
  label,
  cantidad,
  bloques,
  paros_count,
  paros_min,
  tone,
}: {
  label: string;
  cantidad: number;
  bloques: number;
  paros_count: number;
  paros_min: number;
  tone: 'primary' | 'secondary';
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">{label}</p>
      <p
        className={cn(
          'text-2xl font-medium leading-none',
          tone === 'primary' ? 'text-fg' : 'text-fg-secondary',
        )}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatearMiles(Math.round(cantidad))}
      </p>
      <p className="mt-0.5 text-[11px] text-fg-tertiary">
        {bloques} bloque{bloques === 1 ? '' : 's'}
        {paros_count > 0 ? (
          <>
            {' · '}
            {paros_count} paro(s) · <span className="tabular-nums">{Math.round(paros_min)}</span>{' '}
            min
          </>
        ) : null}
      </p>
    </div>
  );
}

function DeltaPill({ delta }: { delta: number | null }) {
  if (delta === null) return null;
  if (delta === 0) {
    return (
      <span className="rounded-full bg-surface-overlay px-1.5 py-0.5 text-[10px] font-medium text-fg-tertiary">
        · 0%
      </span>
    );
  }
  const positivo = delta > 0;
  return (
    <span
      className={cn(
        'rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
        positivo ? 'bg-ok-soft text-ok' : 'bg-crit-soft text-crit',
      )}
    >
      {positivo ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

// =============================================================================
// Helpers compartidos (formato es-CO)
// =============================================================================

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const [y, m, d] = fechaIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}
